import React, { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronDown,
  Info,
  Loader2,
  Plus,
  Upload,
  BookmarkPlus,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TextEditor } from "@/components/TextEditor";
import {
  ScheduleInput,
  type ScheduleEntryValue,
} from "@/components/schedule-input/schedule-input";
import {
  jobOfferDataSchema,
  JobOfferData,
  Schedule,
  calculateDurationMinutes,
  detectUserTimezone,
  OfferWageSource,
  TimeZone,
} from "@/features/job-offer/utils/job-offer-data";
import { FacilitySelect } from "@/components/Selects/facility-select";
import { ProviderSharedSelect } from "@/components/Selects/ProviderSharedSelect";
import { useOrganization } from "@/hooks/use-organization";
import { ProviderPoolSelect } from "@/components/Selects/provider-pool-select";
import { FacilityEditDialog } from "@/features/facility/facility-edit-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WageFrequency, ScheduleType } from "@api/shared-runtime/enums";
import { parseISO, format, parse, eachDayOfInterval } from "date-fns";
import {
  buildRRule,
  type RRuleOptions,
  expandRRule,
} from "@/utils/rrule-helpers";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { ImportScheduleDialog } from "@/features/job-offer/components/import-schedule-dialog";
import { JobOfferTemplateSelect } from "./components/job-offer-template-select";
import { SaveAsTemplateDialog } from "./components/save-as-template-dialog";
import { WageSourceFields } from "./components/wage-source-fields";
import { useActiveOrganizationWageSets } from "@/features/organization-wage-sets/hooks/use-active-organization-wage-sets";

export function JobOfferForm({
  onSave,
  defaultValues,
  onCancel,
  onChange,
}: {
  onSave: (params: { data: JobOfferData; isDraft: boolean }) => Promise<void>;
  defaultValues?: Partial<JobOfferData>;
  onCancel: () => void;
  onChange?: (params: { data: JobOfferData; isDraft: boolean }) => void;
}) {
  const trpc = useTRPC();
  const { selectedOrg, selectedOrgBusinessUnit } = useOrganization();
  const [isFacilityDialogOpen, setIsFacilityDialogOpen] = useState(false);
  const [facilityListVersion, setFacilityListVersion] = useState(0);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Fetch timezone options from the backend
  const { data: timezones } = useQuery(trpc.lookup.getTimezones.queryOptions());

  // Initialize with extended schema and default values
  const form = useForm<JobOfferData>({
    resolver: zodResolver(jobOfferDataSchema),
    defaultValues: {
      ...defaultValues,
      providerPools: [],
      providerUsers: [],
      wageFrequencyId: WageFrequency.Hourly,
      wageSource: defaultValues?.wageSource ?? OfferWageSource.Custom,
      timezoneId: defaultValues?.timezoneId || detectUserTimezone(),
      schedules: defaultValues?.schedules || [],
    },
  });

  const wageFrequencyId = form.watch("wageFrequencyId");
  const isPerShiftWage = wageFrequencyId === WageFrequency.OneTime;
  const wageSource = form.watch("wageSource") ?? OfferWageSource.Custom;
  const isUsingOrganizationWageSet =
    wageSource === OfferWageSource.OrganizationWageSet;
  const {
    data: organizationWageSets = [],
    isLoading: isLoadingOrganizationWageSets,
  } = useActiveOrganizationWageSets(selectedOrgBusinessUnit.id);

  useEffect(() => {
    if (!isPerShiftWage) {
      return;
    }

    form.setValue("regularOvertimeWage", undefined);
    form.setValue("weekendOvertimeWage", undefined);
    form.setValue("onCallOvertimeWage", undefined);
    form.setValue("onCallCallbackOvertimeWage", undefined);
    form.setValue("holidayOvertimeWage", undefined);
  }, [form, isPerShiftWage]);

  // Watch for form changes and call onChange if provided
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (onChange) {
        onChange({ data: value as JobOfferData, isDraft: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [form, onChange]);

  const onSubmit = async (data: JobOfferData, draft: boolean) => {
    setIsSaving(true);
    setSubmissionError(null);
    try {
      await onSave({
        data:
          data.wageFrequencyId === WageFrequency.OneTime
            ? {
                ...data,
                regularOvertimeWage: undefined,
                weekendOvertimeWage: undefined,
                onCallOvertimeWage: undefined,
                onCallCallbackOvertimeWage: undefined,
                holidayOvertimeWage: undefined,
              }
            : data,
        isDraft: draft,
      });
    } catch (error) {
      console.error("Error saving job posting:", error);
      setSubmissionError("Failed to save job offer. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const [isSaving, setIsSaving] = React.useState(false);

  // Collapsible state for optional rate sections
  const [isWeekendRatesOpen, setIsWeekendRatesOpen] = useState(
    () => !!(defaultValues?.weekendWage || defaultValues?.weekendOvertimeWage),
  );
  const [isOnCallRatesOpen, setIsOnCallRatesOpen] = useState(
    () => !!(defaultValues?.onCallWage || defaultValues?.onCallOvertimeWage),
  );
  const [isOnCallCallbackRatesOpen, setIsOnCallCallbackRatesOpen] = useState(
    () =>
      !!(
        defaultValues?.onCallCallbackWage ||
        defaultValues?.onCallCallbackOvertimeWage
      ),
  );
  const [isHolidayRatesOpen, setIsHolidayRatesOpen] = useState(
    () => !!(defaultValues?.holidayWage || defaultValues?.holidayOvertimeWage),
  );

  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);

  /**
   * Convert from ScheduleEntryValue[] (form input component format)
   * to Schedule[] (API format)
   */
  const convertToApiSchedules = (entries: ScheduleEntryValue[]): Schedule[] => {
    const schedules: Schedule[] = [];

    for (const entry of entries) {
      if (!entry.startDate || !entry.startTime || !entry.endTime) {
        continue; // Skip incomplete entries
      }

      const durationMinutes = calculateDurationMinutes(
        entry.startTime,
        entry.endTime,
      );

      if (entry.recurrence.enabled) {
        // Recurring schedule - create one schedule with RRULE
        const startsAt = `${entry.startDate}T${entry.startTime}:00`;

        // For weekly recurrence with multiple days, "count" means weeks, not occurrences
        // So we multiply by the number of selected days to get actual occurrences
        let actualCount: number | undefined;
        if (entry.recurrence.endType === "count") {
          if (
            entry.recurrence.freq === "WEEKLY" &&
            entry.recurrence.byDay &&
            entry.recurrence.byDay.length > 1
          ) {
            actualCount =
              entry.recurrence.count * entry.recurrence.byDay.length;
          } else {
            actualCount = entry.recurrence.count;
          }
        }

        const rruleOptions: RRuleOptions = {
          freq: entry.recurrence.freq,
          interval: entry.recurrence.interval,
          byDay:
            entry.recurrence.freq === "WEEKLY"
              ? entry.recurrence.byDay
              : undefined,
          count: actualCount,
          until:
            entry.recurrence.endType === "until" && entry.recurrence.until
              ? parse(entry.recurrence.until, "yyyy-MM-dd", new Date())
              : undefined,
        };

        schedules.push({
          startsAt,
          durationMinutes,
          rrule: buildRRule(rruleOptions),
          scheduleType: entry.isOnCall
            ? ScheduleType.OnCall
            : ScheduleType.Regular,
        });
      } else {
        // Non-recurring - if there's a date range, create individual schedules for each day
        const startDate = parseISO(entry.startDate);
        const endDate = entry.endDate ? parseISO(entry.endDate) : startDate;

        const days = eachDayOfInterval({ start: startDate, end: endDate });

        for (const day of days) {
          const startsAt = `${format(day, "yyyy-MM-dd")}T${entry.startTime}:00`;
          schedules.push({
            startsAt,
            durationMinutes,
            rrule: null,
            scheduleType: entry.isOnCall
              ? ScheduleType.OnCall
              : ScheduleType.Regular,
          });
        }
      }
    }

    return schedules;
  };

  /**
   * Convert from Schedule[] (API format) to ScheduleEntryValue[] (form input format)
   * This is used when editing an existing job offer
   */
  const convertFromApiSchedules = (
    schedules: Schedule[],
  ): ScheduleEntryValue[] => {
    if (!schedules || schedules.length === 0) {
      // Return default empty entry
      return [
        {
          startDate: null,
          endDate: null,
          startTime: null,
          endTime: null,
          recurrence: {
            enabled: false,
            freq: "WEEKLY",
            interval: 1,
            byDay: [],
            endType: "count",
            count: 4,
            until: null,
          },
          isOnCall: false,
        },
      ];
    }

    // Group schedules by their pattern (same time, same rrule pattern)
    // For now, just convert each schedule to an entry
    return schedules.map((schedule) => {
      const startsAt = parseISO(schedule.startsAt);
      const startDate = format(startsAt, "yyyy-MM-dd");
      const startTime = format(startsAt, "HH:mm");

      // Calculate end time from duration
      const endMinutes =
        startsAt.getHours() * 60 +
        startsAt.getMinutes() +
        schedule.durationMinutes;
      const endHour = Math.floor(endMinutes / 60) % 24;
      const endMin = endMinutes % 60;
      const endTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;

      // Parse RRULE if present
      const hasRRule = !!schedule.rrule;

      return {
        startDate,
        endDate: null,
        startTime,
        endTime,
        recurrence: {
          enabled: hasRRule,
          freq: "WEEKLY" as const, // TODO: parse from rrule
          interval: 1,
          byDay: [],
          endType: "count" as const,
          count: 4,
          until: null,
        },
        isOnCall:
          (schedule.scheduleType ?? ScheduleType.Regular) ===
          ScheduleType.OnCall,
      };
    });
  };

  // Internal state for the schedule input component
  const [scheduleEntries, setScheduleEntries] = useState<ScheduleEntryValue[]>(
    () => convertFromApiSchedules(defaultValues?.schedules || []),
  );

  // Update form when schedule entries change
  const handleScheduleChange = (entries: ScheduleEntryValue[]) => {
    setScheduleEntries(entries);
    const apiSchedules = convertToApiSchedules(entries);
    form.setValue("schedules", apiSchedules, { shouldValidate: true });
  };

  // Handle imported schedules from file
  const handleImportSchedules = useCallback(
    (importedSchedules: ScheduleEntryValue[]) => {
      // Merge imported schedules with existing ones, filtering out empty entries
      const existingNonEmpty = scheduleEntries.filter(
        (entry) => entry.startDate || entry.startTime || entry.endTime,
      );
      const newEntries =
        existingNonEmpty.length > 0
          ? [...existingNonEmpty, ...importedSchedules]
          : importedSchedules;
      handleScheduleChange(newEntries);
    },
    [scheduleEntries, handleScheduleChange],
  );

  // Apply a saved template to the form
  const applyTemplate = useCallback(
    async (templateId: string) => {
      const buId = selectedOrgBusinessUnit?.id;
      if (!buId) {
        return;
      }

      try {
        const template = await queryClient.fetchQuery(
          trpc.jobOffer.templateGet.queryOptions({
            organizationBusinessUnitId: buId,
            id: templateId,
          }),
        );

        // Resolve the facility object from the cached list if a facilityId is stored
        let resolvedFacility: JobOfferData["facility"] | undefined = undefined;
        if (template.facilityId) {
          const facilities = await queryClient.fetchQuery(
            trpc.facility.getAll.queryOptions({
              organizationId: selectedOrg?.id ?? "",
              organizationBusinessUnitId: buId,
            }),
          );
          const match = facilities.find((f) => f.id === template.facilityId);
          if (match) {
            resolvedFacility = {
              id: match.id,
              name: match.name,
              address1: match.address1,
              address2: match.address2 ?? null,
              city: match.city,
              state: match.state,
              zipCode: match.zipCode,
              isRemote: match.isRemote,
            };
          }
        }

        // Resolve provider pool + provider user names from the same queries
        // the selects use, so the chips render real labels (not empty boxes).
        const [pools, profiles] = await Promise.all([
          template.providerPoolIds.length > 0
            ? queryClient.fetchQuery(
                trpc.providerPool.list.queryOptions({
                  organizationBusinessUnitId: buId,
                }),
              )
            : Promise.resolve([]),
          template.userIds.length > 0
            ? queryClient.fetchQuery(
                trpc.profile.list.queryOptions({
                  organizationBusinessUnitId: buId,
                }),
              )
            : Promise.resolve([]),
        ]);

        const poolsById = new Map(pools.map((p) => [p.id, p]));
        const resolvedPools: { id: string; name: string }[] =
          template.providerPoolIds.flatMap((id) => {
            const match = poolsById.get(id);
            return match ? [{ id, name: match.name }] : [];
          });

        const profilesById = new Map(profiles.map((p) => [p.userId, p]));
        const resolvedUsers: { id: string; name: string }[] =
          template.userIds.flatMap((id) => {
            const match = profilesById.get(id);
            if (!match || !match.isRevealed) {
              return [];
            }
            const fullName = `${match.firstName ?? ""} ${match.lastName ?? ""}`
              .trim()
              .replace(/\s+/g, " ");
            const specialty = match.specialties?.[0]?.name || "No specialty";
            return [{ id, name: `${fullName} (${specialty})` }];
          });

        // A template's wage set is only usable while it is still active; otherwise
        // fall back to custom wages and let the user pick.
        const activeOrganizationWageSets = await queryClient.fetchQuery(
          trpc.organizationWageSet.listForNewOffer.queryOptions({
            organizationBusinessUnitId: buId,
          }),
        );
        const templateOrganizationWageSetIsActive =
          activeOrganizationWageSets.some(
            (orgWageSet) =>
              orgWageSet.organizationWageSetId ===
              template.organizationWageSetId,
          );
        const templateUsesOrganizationWageSet =
          !!template.organizationWageSetId &&
          templateOrganizationWageSetIsActive;

        // Reset form values from template
        form.reset({
          ...form.getValues(),
          ...(resolvedFacility ? { facility: resolvedFacility } : {}),
          wageSource: templateUsesOrganizationWageSet
            ? OfferWageSource.OrganizationWageSet
            : OfferWageSource.Custom,
          organizationWageSetId: templateUsesOrganizationWageSet
            ? template.organizationWageSetId
            : null,
          title: template.offerTitle ?? undefined,
          description: template.offerDescription ?? undefined,
          regularWage: template.regularWage ?? undefined,
          regularOvertimeWage: template.regularOvertimeWage ?? undefined,
          weekendWage: template.weekendWage ?? undefined,
          weekendOvertimeWage: template.weekendOvertimeWage ?? undefined,
          onCallWage: template.onCallWage ?? undefined,
          onCallOvertimeWage: template.onCallOvertimeWage ?? undefined,
          onCallCallbackWage: template.onCallCallbackWage ?? undefined,
          onCallCallbackOvertimeWage:
            template.onCallCallbackOvertimeWage ?? undefined,
          holidayWage: template.holidayWage ?? undefined,
          holidayOvertimeWage: template.holidayOvertimeWage ?? undefined,
          wageFrequencyId:
            template.wageFrequencyId === WageFrequency.OneTime ||
            template.wageFrequencyId === WageFrequency.Hourly
              ? template.wageFrequencyId
              : form.getValues("wageFrequencyId"),
          timezoneId: template.timezoneId ?? form.getValues("timezoneId"),
          providerPools: resolvedPools,
          providerUsers: resolvedUsers,
          schedules: [], // will be populated once user fills in dates
        });

        // Templates don't carry date or time-of-day — user fills those in fresh.
        // Preserve only the recurrence-enabled flag and on-call setting per row.
        const newEntries: ScheduleEntryValue[] =
          template.schedules.length > 0
            ? template.schedules.map((s) => ({
                startDate: null,
                endDate: null,
                startTime: null,
                endTime: null,
                recurrence: {
                  enabled: !!s.rrule,
                  freq: "WEEKLY" as const,
                  interval: 1,
                  byDay: [],
                  endType: "count" as const,
                  count: 4,
                  until: null,
                },
                isOnCall:
                  (s.scheduleType ?? ScheduleType.Regular) ===
                  ScheduleType.OnCall,
              }))
            : convertFromApiSchedules([]);

        setScheduleEntries(newEntries);

        // Open collapsible sections that have template values
        if (template.weekendWage || template.weekendOvertimeWage) {
          setIsWeekendRatesOpen(true);
        }
        if (template.onCallWage || template.onCallOvertimeWage) {
          setIsOnCallRatesOpen(true);
        }
        if (
          template.onCallCallbackWage ||
          template.onCallCallbackOvertimeWage
        ) {
          setIsOnCallCallbackRatesOpen(true);
        }
        if (template.holidayWage || template.holidayOvertimeWage) {
          setIsHolidayRatesOpen(true);
        }
      } catch (err) {
        console.error("Failed to apply template:", err);
      }
    },
    [
      selectedOrg,
      selectedOrgBusinessUnit,
      trpc,
      form,
      convertFromApiSchedules,
      setScheduleEntries,
      setIsWeekendRatesOpen,
      setIsOnCallRatesOpen,
      setIsOnCallCallbackRatesOpen,
      setIsHolidayRatesOpen,
    ],
  );

  // Calculate total shifts grouped by duration from the schedule entries
  const shiftSummary = React.useMemo(() => {
    // Map of durationMinutes -> count of shifts
    const durationGroups = new Map<number, number>();

    for (const entry of scheduleEntries) {
      if (!entry.startDate || !entry.startTime || !entry.endTime) {
        continue;
      }

      const entryDuration = calculateDurationMinutes(
        entry.startTime,
        entry.endTime,
      );
      let shiftCount = 0;

      if (entry.recurrence.enabled && entry.startDate) {
        // For weekly recurrence with multiple days, "count" means weeks, not occurrences
        // So we multiply by the number of selected days to get actual occurrences
        let actualCount: number | undefined;
        if (entry.recurrence.endType === "count") {
          if (
            entry.recurrence.freq === "WEEKLY" &&
            entry.recurrence.byDay &&
            entry.recurrence.byDay.length > 1
          ) {
            actualCount =
              entry.recurrence.count * entry.recurrence.byDay.length;
          } else {
            actualCount = entry.recurrence.count;
          }
        }

        // Build RRULE options based on end type
        const rruleOptions: RRuleOptions = {
          freq: entry.recurrence.freq,
          interval: entry.recurrence.interval,
          byDay:
            entry.recurrence.freq === "WEEKLY"
              ? entry.recurrence.byDay
              : undefined,
          count: actualCount,
          until:
            entry.recurrence.endType === "until" && entry.recurrence.until
              ? parse(entry.recurrence.until, "yyyy-MM-dd", new Date())
              : undefined,
        };

        const rrule = buildRRule(rruleOptions);
        if (rrule) {
          const startDate = parse(entry.startDate, "yyyy-MM-dd", new Date());
          // For count-based rules, use a far future date as window end
          // The rrule library will respect the COUNT limit
          const windowEnd = entry.recurrence.until
            ? parse(entry.recurrence.until, "yyyy-MM-dd", new Date())
            : new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year

          const occurrences = expandRRule(
            rrule,
            startDate,
            startDate,
            windowEnd,
          );
          shiftCount = occurrences.length;
        }
      } else {
        // Non-recurring: count days in range
        const startDate = parseISO(entry.startDate);
        const endDate = entry.endDate ? parseISO(entry.endDate) : startDate;
        const days = eachDayOfInterval({ start: startDate, end: endDate });
        shiftCount = days.length;
      }

      if (shiftCount > 0) {
        durationGroups.set(
          entryDuration,
          (durationGroups.get(entryDuration) ?? 0) + shiftCount,
        );
      }
    }

    // Format duration for display
    const formatDuration = (minutes: number): string => {
      const hours = minutes / 60;
      if (hours === Math.floor(hours)) {
        return `${hours} hour${hours === 1 ? "" : "s"}`;
      }
      return `${hours.toFixed(1)} hours`;
    };

    // Build groups array sorted by duration (descending)
    const groups = Array.from(durationGroups.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([duration, count]) => ({
        count,
        durationStr: formatDuration(duration),
      }));

    const total = groups.reduce((sum, g) => sum + g.count, 0);

    return { total, groups };
  }, [scheduleEntries]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => onSubmit(data, false))}
        className="space-y-6"
      >
        <JobOfferTemplateSelect onApply={applyTemplate} />

        <Card className="shadow-xs">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 pt-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="facility"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">Facility</FormLabel>
                    <FormControl className="flex-1">
                      <FacilitySelect
                        key={facilityListVersion}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        placeholder="Select facility"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => setIsFacilityDialogOpen(true)}
                  className="h-13"
                  aria-label="Add new facility"
                >
                  <Plus className="h-4 w-4" /> Add New
                </Button>
                <FacilityEditDialog
                  facility={null}
                  isOpen={isFacilityDialogOpen}
                  onOpenChange={setIsFacilityDialogOpen}
                  onSuccess={(newFacility) => {
                    setIsFacilityDialogOpen(false);
                    form.setValue(
                      "facility",
                      {
                        id: newFacility.id,
                        name: newFacility.name,
                        address1: newFacility.address1,
                        address2: newFacility.address2 ?? null,
                        city: newFacility.city,
                        state: newFacility.state,
                        zipCode: newFacility.zipCode,
                        isRemote: newFacility.isRemote ?? false,
                      },
                      { shouldValidate: true },
                    );
                    setFacilityListVersion((v) => v + 1);
                  }}
                />
              </div>
            </div>

            <WageSourceFields
              wageSource={wageSource}
              onWageSourceChange={(value) => form.setValue("wageSource", value)}
              organizationWageSetId={form.watch("organizationWageSetId")}
              onOrganizationWageSetIdChange={(value) =>
                form.setValue("organizationWageSetId", value, {
                  shouldValidate: true,
                })
              }
              organizationWageSets={organizationWageSets}
              isLoadingOrganizationWageSets={isLoadingOrganizationWageSets}
              error={form.formState.errors.organizationWageSetId?.message}
            />

            {!isUsingOrganizationWageSet && (
              <>
                <FormField
                  control={form.control}
                  name="wageFrequencyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-medium">
                        Wage Frequency
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={WageFrequency.OneTime}>
                            Per Shift
                          </SelectItem>
                          <SelectItem value={WageFrequency.Hourly}>
                            Hourly
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-6">
                  {/* Regular Rates - Always visible */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Regular Rates
                    </h3>
                    <div
                      className={`grid grid-cols-1 gap-4 ${
                        isPerShiftWage ? "" : "sm:grid-cols-2"
                      }`}
                    >
                      <FormField
                        control={form.control}
                        name="regularWage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-medium">
                              Regular Wage{" "}
                              <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                  $
                                </span>
                                <Input
                                  type="number"
                                  {...field}
                                  value={field.value ?? ""}
                                  className="focus-visible:ring-blue-500 pl-7"
                                  placeholder="100"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {!isPerShiftWage && (
                        <FormField
                          control={form.control}
                          name="regularOvertimeWage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="font-medium">
                                Regular Overtime Wage
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                    $
                                  </span>
                                  <Input
                                    type="number"
                                    {...field}
                                    value={field.value ?? ""}
                                    className="focus-visible:ring-blue-500 pl-7"
                                    placeholder="150"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </div>

                  {/* Weekend Rates - Collapsible */}
                  <Collapsible
                    open={isWeekendRatesOpen}
                    onOpenChange={setIsWeekendRatesOpen}
                  >
                    <CollapsibleTrigger className="flex w-full items-center justify-between group">
                      <h3 className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                        Weekend Rates
                      </h3>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                          isWeekendRatesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-4">
                      <div
                        className={`grid grid-cols-1 gap-4 ${
                          isPerShiftWage ? "" : "sm:grid-cols-2"
                        }`}
                      >
                        <FormField
                          control={form.control}
                          name="weekendWage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="font-medium">
                                Weekend Wage
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                    $
                                  </span>
                                  <Input
                                    type="number"
                                    {...field}
                                    value={field.value ?? ""}
                                    className="focus-visible:ring-blue-500 pl-7"
                                    placeholder="120"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {!isPerShiftWage && (
                          <FormField
                            control={form.control}
                            name="weekendOvertimeWage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-medium">
                                  Weekend Overtime Wage
                                </FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                      $
                                    </span>
                                    <Input
                                      type="number"
                                      {...field}
                                      value={field.value ?? ""}
                                      className="focus-visible:ring-blue-500 pl-7"
                                      placeholder="180"
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* On-Call Rates - Collapsible */}
                  <Collapsible
                    open={isOnCallRatesOpen}
                    onOpenChange={setIsOnCallRatesOpen}
                  >
                    <CollapsibleTrigger className="flex w-full items-center justify-between group">
                      <h3 className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                        On-Call Rates
                      </h3>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                          isOnCallRatesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-4">
                      <div
                        className={`grid grid-cols-1 gap-4 ${
                          isPerShiftWage ? "" : "sm:grid-cols-2"
                        }`}
                      >
                        <FormField
                          control={form.control}
                          name="onCallWage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="font-medium">
                                On-Call Wage
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                    $
                                  </span>
                                  <Input
                                    type="number"
                                    {...field}
                                    value={field.value ?? ""}
                                    className="focus-visible:ring-blue-500 pl-7"
                                    placeholder="80"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {!isPerShiftWage && (
                          <FormField
                            control={form.control}
                            name="onCallOvertimeWage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-medium">
                                  On-Call Overtime Wage
                                </FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                      $
                                    </span>
                                    <Input
                                      type="number"
                                      {...field}
                                      value={field.value ?? ""}
                                      className="focus-visible:ring-blue-500 pl-7"
                                      placeholder="120"
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* On-Call Callback Rates - Collapsible */}
                  <Collapsible
                    open={isOnCallCallbackRatesOpen}
                    onOpenChange={setIsOnCallCallbackRatesOpen}
                  >
                    <CollapsibleTrigger className="flex w-full items-center justify-between group">
                      <h3 className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                        On-Call Callback Rates
                      </h3>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                          isOnCallCallbackRatesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-4">
                      <div
                        className={`grid grid-cols-1 gap-4 ${
                          isPerShiftWage ? "" : "sm:grid-cols-2"
                        }`}
                      >
                        <FormField
                          control={form.control}
                          name="onCallCallbackWage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="font-medium">
                                On-Call Callback Wage
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                    $
                                  </span>
                                  <Input
                                    type="number"
                                    {...field}
                                    value={field.value ?? ""}
                                    className="focus-visible:ring-blue-500 pl-7"
                                    placeholder="130"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {!isPerShiftWage && (
                          <FormField
                            control={form.control}
                            name="onCallCallbackOvertimeWage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-medium">
                                  On-Call Callback Overtime Wage
                                </FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                      $
                                    </span>
                                    <Input
                                      type="number"
                                      {...field}
                                      value={field.value ?? ""}
                                      className="focus-visible:ring-blue-500 pl-7"
                                      placeholder="195"
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Holiday Rates - Collapsible */}
                  <Collapsible
                    open={isHolidayRatesOpen}
                    onOpenChange={setIsHolidayRatesOpen}
                  >
                    <CollapsibleTrigger className="flex w-full items-center justify-between group">
                      <h3 className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                        Holiday Rates
                      </h3>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                          isHolidayRatesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-4">
                      <div
                        className={`grid grid-cols-1 gap-4 ${
                          isPerShiftWage ? "" : "sm:grid-cols-2"
                        }`}
                      >
                        <FormField
                          control={form.control}
                          name="holidayWage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="font-medium">
                                Holiday Wage
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                    $
                                  </span>
                                  <Input
                                    type="number"
                                    {...field}
                                    value={field.value ?? ""}
                                    className="focus-visible:ring-blue-500 pl-7"
                                    placeholder="200"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {!isPerShiftWage && (
                          <FormField
                            control={form.control}
                            name="holidayOvertimeWage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="font-medium">
                                  Holiday Overtime Wage
                                </FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                      $
                                    </span>
                                    <Input
                                      type="number"
                                      {...field}
                                      value={field.value ?? ""}
                                      className="focus-visible:ring-blue-500 pl-7"
                                      placeholder="250"
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Card className="shadow-xs">
          <CardHeader className="bg-gray-50 border-b flex flex-row items-center justify-between">
            <CardTitle className="text-lg">
              Schedule <span className="text-red-500">*</span>
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsImportDialogOpen(true)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import from File
            </Button>
          </CardHeader>
          <CardContent className="space-y-6 pt-5">
            {shiftSummary.total > 0 && (
              <div className="text-gray-600">
                {shiftSummary.groups.map((group, index) => (
                  <span key={group.durationStr}>
                    {index > 0 && ", "}
                    <span className="font-semibold text-gray-900">
                      {group.count}
                    </span>{" "}
                    shift{group.count === 1 ? "" : "s"} of{" "}
                    <span className="font-semibold text-gray-900">
                      {group.durationStr}
                    </span>
                  </span>
                ))}
              </div>
            )}
            <FormField
              control={form.control}
              name="schedules"
              render={() => (
                <FormItem>
                  <FormControl>
                    <ScheduleInput
                      value={scheduleEntries}
                      onChange={handleScheduleChange}
                      timezoneId={form.watch("timezoneId")}
                      onTimezoneChange={(tz) =>
                        form.setValue("timezoneId", tz as TimeZone)
                      }
                      timezones={timezones}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card className="shadow-xs">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-lg">Job Details (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pt-5">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium">Job Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter job title"
                      onChange={field.onChange}
                      value={field.value || ""}
                      onBlur={field.onBlur}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium">Description</FormLabel>
                  <FormControl>
                    <TextEditor
                      contentHTML={field.value || ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Card className="shadow-xs">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-lg">Send to the following</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pt-5">
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-2">
                <p className="text-amber-800 text-sm flex items-center">
                  <Info className="h-4 w-4 mr-2" />
                  Note: It will go to all the entities selected below. If you
                  select a pool or multiple people, this job offer will go to
                  the first person that accepts it. Only 1 person can accept the
                  job offer.
                </p>
              </div>

              <div className="pt-2">
                <FormField
                  control={form.control}
                  name="providerPools"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-medium">
                        Provider Pools
                      </FormLabel>
                      <FormControl>
                        <ProviderPoolSelect
                          isMulti={true}
                          value={field.value || []}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="pt-2">
                <FormField
                  control={form.control}
                  name="providerUsers"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-medium">Providers</FormLabel>
                      <FormControl>
                        <ProviderSharedSelect
                          value={field.value}
                          onChange={field.onChange}
                          organizationBusinessUnitId={
                            selectedOrgBusinessUnit?.id || ""
                          }
                          isMulti={true}
                          placeholder="Select providers"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="pt-2">
                <FormLabel className="font-medium">
                  Acceptance window (optional)
                </FormLabel>
                <p className="text-sm text-gray-500 mb-2">
                  Providers will not be able to accept after this duration.
                  Leave blank for no deadline.
                </p>
                <div className="flex items-start gap-2">
                  <FormField
                    control={form.control}
                    name="expiresInDuration"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            placeholder="e.g. 24"
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              field.onChange(v === "" ? null : Number(v));
                            }}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="expiresInDurationUnit"
                    render={({ field }) => (
                      <FormItem className="w-40">
                        <FormControl>
                          <Select
                            value={field.value ?? ""}
                            onValueChange={(v) =>
                              field.onChange(v as "minutes" | "hours" | "days")
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Unit" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="minutes">Minutes</SelectItem>
                              <SelectItem value="hours">Hours</SelectItem>
                              <SelectItem value="days">Days</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {submissionError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <p>{submissionError}</p>
          </div>
        )}

        <Card className="shadow-xs border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row justify-start gap-3">
              <Button
                type="submit"
                disabled={isSaving}
                className="w-full sm:w-auto py-6 px-10 text-lg"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Request"
                )}
              </Button>

              <Button
                variant="outline"
                type="button"
                className="w-full sm:w-auto py-6 text-lg"
                onClick={onCancel}
              >
                Cancel
              </Button>

              <Button
                variant="outline"
                type="button"
                className="w-full sm:w-auto py-6 text-lg"
                onClick={() => setIsSaveTemplateOpen(true)}
              >
                <BookmarkPlus className="mr-2 h-5 w-5" />
                Save as Template
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      <ImportScheduleDialog
        isOpen={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImport={handleImportSchedules}
      />

      <SaveAsTemplateDialog
        isOpen={isSaveTemplateOpen}
        onOpenChange={setIsSaveTemplateOpen}
        getFormValues={() => form.getValues()}
      />
    </Form>
  );
}
